package ai.wagemix.web.components

import ai.wagemix.web.theme.WMButtonPaddingInlineSmVar
import ai.wagemix.web.theme.WMButtonPaddingInlineVar
import ai.wagemix.web.theme.WMControlCornerVar
import ai.wagemix.web.theme.WMControlHeightVar
import ai.wagemix.web.theme.WMControlSmHeightVar
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMIconSizeVar
import ai.wagemix.web.theme.WMIconSmVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMOpacity
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import ai.wagemix.web.theme.WMTightGapVar
import com.fsryan.foundation.web.components.FSUnoutlinedTextButtonVariant
import com.fsryan.foundation.web.components.buttonVariant
import com.fsryan.foundation.web.components.textButtonVariant
import com.fsryan.foundation.web.theme.FSWebTheme
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonKind
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.selectors.disabled
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import com.varabyte.kobweb.compose.css.WhiteSpace
import org.jetbrains.compose.web.css.px

/** Penpot `Wagemix / Button`, property `Kind`. */
enum class WMButtonKind {
    /** The one brand-blue action on a surface. */
    Primary,
    /** Outlined, neutral. */
    Secondary,
    /** Text only, no border. */
    Ghost,
    /** Destructive. */
    Danger
}

/** Penpot `Wagemix / Button`, property `Size`. */
enum class WMButtonSize {
    /** 40px control height. */
    Md,
    /** 32px control height. */
    Sm
}

/*
 * Variants over Silk's ButtonStyle, chained after foundation's own use-variant so they win:
 * the 40/32px control heights (foundation's default is 48), the 8px control corner, the
 * design's label text and the neutral outline of the secondary kind.
 */
val WMMdButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .setVariable(ButtonVars.Height, WMControlHeightVar.value())
            .setVariable(ButtonVars.PaddingHorizontal, WMButtonPaddingInlineVar.value())
            .minHeight(WMControlHeightVar.value())
            .height(WMControlHeightVar.value())
            .borderRadius(WMControlCornerVar.value())
            .gap(WMInlineGapVar.value())
            .whiteSpace(WhiteSpace.NoWrap)
    }
    disabled { Modifier.opacity(WMOpacity.DISABLED) }
}

val WMSmButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .setVariable(ButtonVars.Height, WMControlSmHeightVar.value())
            .setVariable(ButtonVars.PaddingHorizontal, WMButtonPaddingInlineSmVar.value())
            .minHeight(WMControlSmHeightVar.value())
            .height(WMControlSmHeightVar.value())
            .borderRadius(WMControlCornerVar.value())
            .gap(WMInlineGapVar.value())
            .whiteSpace(WhiteSpace.NoWrap)
    }
    disabled { Modifier.opacity(WMOpacity.DISABLED) }
}

/** The secondary kind: surface fill, `outline` border, `onSurface` text. */
val WMSecondaryButtonVariant = ButtonStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .setVariable(ButtonVars.Color, colors.onSurface)
            .setVariable(ButtonVars.BackgroundDefaultColor, colors.surface)
            .setVariable(ButtonVars.BackgroundHoverColor, colors.surfaceContainer)
            .setVariable(ButtonVars.BackgroundPressedColor, colors.surfaceContainerHigh)
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.outline)
    }
}

/** The ghost kind: no fill, no border, `onSurfaceVariant` text. */
val WMGhostButtonVariant = ButtonStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .setVariable(ButtonVars.Color, colors.onSurfaceVariant)
            .setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
            .setVariable(ButtonVars.BackgroundHoverColor, colors.surfaceContainer)
            .setVariable(ButtonVars.BackgroundPressedColor, colors.surfaceContainerHigh)
            .border(0.px)
    }
}

/** The ghost kind on a primary-container surface (active sidebar item). */
val WMGhostOnPrimaryContainerButtonVariant = ButtonStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .setVariable(ButtonVars.Color, colors.onPrimaryContainer)
            .setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
            .setVariable(ButtonVars.BackgroundHoverColor, colors.primaryContainer)
            .setVariable(ButtonVars.BackgroundPressedColor, colors.primaryContainer)
            .border(0.px)
    }
}

internal fun WMButtonSize.variant(): CssStyleVariant<ButtonKind> = when (this) {
    WMButtonSize.Md -> WMMdButtonVariant
    WMButtonSize.Sm -> WMSmButtonVariant
}

internal fun WMButtonSize.iconSize(): CSSLengthNumericValue = when (this) {
    WMButtonSize.Md -> WMIconSizeVar.value()
    WMButtonSize.Sm -> WMIconSmVar.value()
}

internal fun WMButtonSize.side(): CSSLengthNumericValue = when (this) {
    WMButtonSize.Md -> WMControlHeightVar.value()
    WMButtonSize.Sm -> WMControlSmHeightVar.value()
}

/** The full Silk variant chain for a kind and size, built on foundation's use-variants. */
fun WMButtonKind.variant(size: WMButtonSize): CssStyleVariant<ButtonKind> = when (this) {
    WMButtonKind.Primary -> FSWebTheme.Use.Primary.buttonVariant().then(size.variant())
    WMButtonKind.Danger -> FSWebTheme.Use.Alert.buttonVariant().then(size.variant())
    WMButtonKind.Secondary -> FSWebTheme.Use.Primary.textButtonVariant().then(size.variant()).then(WMSecondaryButtonVariant)
    WMButtonKind.Ghost -> FSWebTheme.Use.Primary.textButtonVariant()
        .then(FSUnoutlinedTextButtonVariant)
        .then(size.variant())
        .then(WMGhostButtonVariant)
}

/**
 * Penpot `Wagemix / Button`. A Silk `Button` styled through foundation's use-variants plus the
 * Wagemix size and kind variants, so focus, disabled state and keyboard handling stay in one
 * place. [text] is the label; [leadingIcon] and [trailingIcon] are optional decorative glyphs.
 */
@Composable
fun WMButton(
    text: String,
    modifier: Modifier = Modifier,
    kind: WMButtonKind = WMButtonKind.Primary,
    size: WMButtonSize = WMButtonSize.Md,
    enabled: Boolean = true,
    fullWidth: Boolean = false,
    leadingIcon: WMIconGlyph? = null,
    trailingIcon: WMIconGlyph? = null,
    onClick: () -> Unit
) {
    Button(
        onClick = { onClick() },
        modifier = WMLabelStrongTextStyle.toModifier()
            .whiteSpace(WhiteSpace.NoWrap)
            .flexShrink(0)
            .then(if (fullWidth) Modifier.fillMaxWidth() else Modifier)
            .then(modifier),
        variant = kind.variant(size),
        enabled = enabled
    ) {
        // Silk lays the content out in its own row inside the <button>, so a gap on the button never reaches the icon
        // and the label; this row carries it.
        Row(modifier = Modifier.gap(WMTightGapVar.value()), verticalAlignment = Alignment.CenterVertically) {
            leadingIcon?.let { WMIcon(it, accessibilityString = null, size = size.iconSize()) }
            SpanText(text)
            trailingIcon?.let { WMIcon(it, accessibilityString = null, size = size.iconSize()) }
        }
    }
}

/**
 * Penpot `Wagemix / Icon Button`: a square button holding one glyph. [accessibilityString]
 * names the action for assistive technology and is required.
 */
@Composable
fun WMIconButton(
    glyph: WMIconGlyph,
    accessibilityString: String,
    modifier: Modifier = Modifier,
    kind: WMButtonKind = WMButtonKind.Ghost,
    size: WMButtonSize = WMButtonSize.Md,
    enabled: Boolean = true,
    onClick: () -> Unit
) {
    Button(
        onClick = { onClick() },
        modifier = Modifier
            .size(size.side())
            .setVariable(ButtonVars.PaddingHorizontal, 0.px)
            .padding(0.px)
            .ariaLabel(accessibilityString)
            .then(modifier),
        variant = kind.variant(size),
        enabled = enabled
    ) {
        WMIcon(glyph, accessibilityString = null, size = size.iconSize())
    }
}

/** A button whose content is supplied by the caller (for example a label plus a badge). */
@Composable
fun WMButton(
    modifier: Modifier = Modifier,
    kind: WMButtonKind = WMButtonKind.Primary,
    size: WMButtonSize = WMButtonSize.Md,
    enabled: Boolean = true,
    onClick: () -> Unit,
    content: @Composable RowScope.() -> Unit
) {
    Button(
        onClick = { onClick() },
        modifier = WMLabelStrongTextStyle.toModifier().then(modifier),
        variant = kind.variant(size),
        enabled = enabled,
        content = content
    )
}
